/**
 * 2D constraint solver using the Levenberg-Marquardt algorithm (damped
 * Gauss-Newton). This is an "own implementation" (no external linear-
 * algebra dependency) — chosen over SolveSpace/WASM because AutoCAD
 * dynamic block constraint systems are small (typical 5-30 variables,
 * 5-30 constraints) where a lightweight hand-written LM is both fast
 * and straightforward to audit.
 *
 * Algorithm outline:
 *
 *   x_0 = initial variable values
 *   λ   = dampingFactor (initial)
 *   for iter in 1..maxIterations:
 *     r  = residuals.compute(x)       ∈ ℝ^m
 *     J  = residuals.jacobian(x)      ∈ ℝ^{m × n}
 *     H  = Jᵀ·J + λ·I                 (damped normal equations)
 *     g  = Jᵀ·r
 *     solve H·dx = -g   (symmetric positive-definite — Cholesky)
 *     x' = x + dx
 *     r' = residuals.compute(x')
 *     if |r'| < |r|:
 *       accept (x = x', λ /= 2)
 *     else:
 *       reject (λ *= 10)
 *     if |r| < tolerance:        → success
 *     if λ > maxDamping:         → failure (infeasible)
 *     if |dx| < minStep:         → stuck, stop
 *
 * Fixed variables (Fix geometric constraint):
 *   - removed from the active variable set before assembling Jᵀ·J,
 *   - held constant throughout iteration,
 *   - still appear in the final result map with their initial value.
 *
 * The code path is deliberately straight-line and allocation-light in
 * the hot loop (reusing pre-sized Float64Array buffers for J, H, g, dx).
 * Target: < 100 ms for 20 vars × 20 constraints with ≤50 iterations on
 * a standard laptop — well within a 60 Hz UX budget.
 *
 * See `constraintSolver.types.ts` for the public protocol. See
 * `constraintResiduals.ts` for the geometric-constraint residual
 * builders that feed this solver.
 */

import type {
  ConstraintResidual,
  ConstraintVariable,
  SolveInput,
  SolveResult,
  SolverOptions,
} from "./constraintSolver.types";

// ═══════════════════════════════════════════════════════════════════════
// Defaults
// ═══════════════════════════════════════════════════════════════════════

const DEFAULT_MAX_ITERATIONS = 50;
const DEFAULT_TOLERANCE = 1e-8;
const DEFAULT_DAMPING = 1e-6;
const DEFAULT_MAX_DAMPING = 1e12;
const DEFAULT_MIN_STEP = 1e-12;

function withDefaults(
  options: SolverOptions | undefined,
): Required<SolverOptions> {
  return {
    maxIterations: options?.maxIterations ?? DEFAULT_MAX_ITERATIONS,
    tolerance: options?.tolerance ?? DEFAULT_TOLERANCE,
    dampingFactor: options?.dampingFactor ?? DEFAULT_DAMPING,
    maxDamping: options?.maxDamping ?? DEFAULT_MAX_DAMPING,
    minStep: options?.minStep ?? DEFAULT_MIN_STEP,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// Linear algebra — small-matrix primitives
// ═══════════════════════════════════════════════════════════════════════
//
// Matrices stored row-major in Float64Array (n×n or m×n). We avoid
// numeric.js / ml-matrix etc. — sizes here peak at ~30×30 which is
// trivial for hand-rolled Cholesky or Gauss.

/**
 * Cholesky decomposition for a symmetric positive-definite matrix A =
 * L·Lᵀ, then solves A·x = b in-place. Returns `true` on success, `false`
 * if the matrix is singular (non-positive diagonal encountered — caller
 * should bump λ and retry).
 *
 * Operates on a single flat buffer of size n². Overwrites A with L
 * (lower triangle). b is the right-hand side in & solved vector out.
 */
function choleskySolve(A: Float64Array, b: Float64Array, n: number): boolean {
  // Factor in place: L[i][j] for i ≥ j.
  for (let j = 0; j < n; j++) {
    let sum = A[j * n + j]!;
    for (let k = 0; k < j; k++) {
      const v = A[j * n + k]!;
      sum -= v * v;
    }
    if (sum <= 1e-30) return false; // not PD
    const Ljj = Math.sqrt(sum);
    A[j * n + j] = Ljj;
    for (let i = j + 1; i < n; i++) {
      let s = A[i * n + j]!;
      for (let k = 0; k < j; k++) {
        s -= A[i * n + k]! * A[j * n + k]!;
      }
      A[i * n + j] = s / Ljj;
    }
  }
  // Forward solve L·y = b (overwrite b with y).
  for (let i = 0; i < n; i++) {
    let s = b[i]!;
    for (let k = 0; k < i; k++) {
      s -= A[i * n + k]! * b[k]!;
    }
    b[i] = s / A[i * n + i]!;
  }
  // Back solve Lᵀ·x = y (overwrite b with x).
  for (let i = n - 1; i >= 0; i--) {
    let s = b[i]!;
    for (let k = i + 1; k < n; k++) {
      s -= A[k * n + i]! * b[k]!;
    }
    b[i] = s / A[i * n + i]!;
  }
  return true;
}

/**
 * Fallback — Gaussian elimination with partial pivoting for dense, not-
 * necessarily-PD matrices. Used when Cholesky fails (rare: singular H
 * at the damped stage). Same signature as choleskySolve.
 */
function gaussianSolve(A: Float64Array, b: Float64Array, n: number): boolean {
  // Forward elimination with partial pivoting.
  for (let i = 0; i < n; i++) {
    // Find pivot.
    let pivotRow = i;
    let pivotAbs = Math.abs(A[i * n + i]!);
    for (let r = i + 1; r < n; r++) {
      const v = Math.abs(A[r * n + i]!);
      if (v > pivotAbs) {
        pivotAbs = v;
        pivotRow = r;
      }
    }
    if (pivotAbs < 1e-30) return false;
    // Swap rows i and pivotRow.
    if (pivotRow !== i) {
      for (let c = 0; c < n; c++) {
        const tmp = A[i * n + c]!;
        A[i * n + c] = A[pivotRow * n + c]!;
        A[pivotRow * n + c] = tmp;
      }
      const tmpB = b[i]!;
      b[i] = b[pivotRow]!;
      b[pivotRow] = tmpB;
    }
    // Eliminate below.
    const pivot = A[i * n + i]!;
    for (let r = i + 1; r < n; r++) {
      const factor = A[r * n + i]! / pivot;
      if (factor === 0) continue;
      for (let c = i; c < n; c++) {
        A[r * n + c] = A[r * n + c]! - factor * A[i * n + c]!;
      }
      b[r] = b[r]! - factor * b[i]!;
    }
  }
  // Back substitution.
  for (let i = n - 1; i >= 0; i--) {
    let s = b[i]!;
    for (let c = i + 1; c < n; c++) {
      s -= A[i * n + c]! * b[c]!;
    }
    const diag = A[i * n + i]!;
    if (Math.abs(diag) < 1e-30) return false;
    b[i] = s / diag;
  }
  return true;
}

// ═══════════════════════════════════════════════════════════════════════
// Solver core
// ═══════════════════════════════════════════════════════════════════════

/**
 * Build a lookup: active-variable-id → column index in the solver's
 * internal dense buffers. Fixed variables get `-1`. Returned alongside
 * the inverse map (column → id) for diagnostics.
 */
function buildIndex(variables: readonly ConstraintVariable[]): {
  indexOf: Map<string, number>;
  ids: readonly string[];
  n: number;
  allValues: Record<string, number>;
} {
  const indexOf = new Map<string, number>();
  const ids: string[] = [];
  const allValues: Record<string, number> = {};
  let col = 0;
  for (const v of variables) {
    allValues[v.id] = v.initialValue;
    if (v.fixed === true) continue;
    indexOf.set(v.id, col);
    ids.push(v.id);
    col++;
  }
  return { indexOf, ids, n: col, allValues };
}

/**
 * Compute WEIGHTED residual vector r ∈ ℝ^m at the current variable
 * snapshot. Residual weight (default 1) multiplies each scalar so the
 * downstream ‖r‖² is the weighted sum of squares.
 */
function computeResidualVector(
  residuals: readonly ConstraintResidual[],
  values: Readonly<Record<string, number>>,
  out: Float64Array,
): void {
  for (let i = 0; i < residuals.length; i++) {
    const res = residuals[i]!;
    const w = res.weight ?? 1;
    out[i] = w * res.compute(values);
  }
}

/**
 * L2 norm squared of a vector (avoids sqrt in the inner loop — caller
 * takes sqrt for the final report when needed).
 */
function normSq(v: Float64Array, m: number): number {
  let s = 0;
  for (let i = 0; i < m; i++) s += v[i]! * v[i]!;
  return s;
}

/**
 * Assemble Jᵀ·J + λ·I into H (n×n) and Jᵀ·r into g (n) in a single
 * pass. Jacobian is sparse per-residual (Record<string, number>); we
 * translate to the dense column index on the fly so large systems with
 * few non-zeros per row stay cheap.
 *
 * H is overwritten; g is overwritten.
 */
function assembleNormalEquations(
  residuals: readonly ConstraintResidual[],
  values: Readonly<Record<string, number>>,
  r: Float64Array,
  indexOf: Map<string, number>,
  lambda: number,
  n: number,
  H: Float64Array,
  g: Float64Array,
): void {
  H.fill(0);
  g.fill(0);

  // Accumulate Jᵀ·J and Jᵀ·r via per-residual outer products:
  //   Jᵀ·J = Σ_k (J_k)ᵀ (J_k)    where J_k is row k (sparse)
  //   Jᵀ·r = Σ_k (J_k)ᵀ r_k
  for (let k = 0; k < residuals.length; k++) {
    const res = residuals[k]!;
    const row = res.jacobian(values);
    const w = res.weight ?? 1;
    // `r` was already scaled by weight during computeResidualVector, so
    // r[k] = w·residual. For the normal equations we need Jᵀ·r where the
    // effective jacobian row is w·J_original; the outer product then
    // picks up another factor of w. Practical form: multiply each
    // non-zero by w before accumulating.
    const rk = r[k]!;
    const nonZeros: Array<{ idx: number; val: number }> = [];
    for (const id in row) {
      const idx = indexOf.get(id);
      if (idx === undefined) continue; // fixed variable — drops out
      const val = row[id]! * w;
      if (val === 0) continue;
      nonZeros.push({ idx, val });
    }
    for (let a = 0; a < nonZeros.length; a++) {
      const { idx: ia, val: va } = nonZeros[a]!;
      g[ia] = g[ia]! + va * rk;
      for (let b = a; b < nonZeros.length; b++) {
        const { idx: ib, val: vb } = nonZeros[b]!;
        const contribution = va * vb;
        H[ia * n + ib] = H[ia * n + ib]! + contribution;
        if (ib !== ia) {
          H[ib * n + ia] = H[ib * n + ia]! + contribution;
        }
      }
    }
  }

  // Damping — add λ on the diagonal (Marquardt variant). Classical
  // Levenberg adds λ·diag(JᵀJ) but we keep the simpler +λI form which
  // is standard for small, well-scaled systems.
  for (let i = 0; i < n; i++) {
    H[i * n + i] = H[i * n + i]! + lambda;
  }
}

/**
 * Main entry point — run Levenberg-Marquardt until convergence, divergence,
 * or maxIterations.
 *
 * Reports:
 *   - success=true + residualNorm ≤ tolerance: converged
 *   - success=false with last accepted state: λ hit maxDamping, or the
 *     residual never shrank, or the step became numerically unresolvable.
 */
export function solveConstraints(input: SolveInput): SolveResult {
  const opts = withDefaults(input.options);
  const { variables, residuals } = input;

  // Degenerate: no residuals → nothing to solve, success trivially.
  if (residuals.length === 0) {
    const values: Record<string, number> = {};
    for (const v of variables) values[v.id] = v.initialValue;
    return {
      success: true,
      iterations: 0,
      residualNorm: 0,
      variables: values,
    };
  }

  const { indexOf, ids, n, allValues } = buildIndex(variables);

  // Edge case: all variables fixed. Evaluate residual once and report.
  if (n === 0) {
    const r = new Float64Array(residuals.length);
    computeResidualVector(residuals, allValues, r);
    const rNorm = Math.sqrt(normSq(r, residuals.length));
    return {
      success: rNorm <= opts.tolerance,
      iterations: 0,
      residualNorm: rNorm,
      variables: allValues,
      errors:
        rNorm > opts.tolerance
          ? ["All variables fixed but constraints not satisfied"]
          : [],
    };
  }

  const m = residuals.length;

  // Hot-loop buffers.
  const r = new Float64Array(m);
  const rTrial = new Float64Array(m);
  const H = new Float64Array(n * n);
  const HCopy = new Float64Array(n * n);
  const g = new Float64Array(n);
  const dx = new Float64Array(n);

  // Current state copied into `values` so we can mutate it in place
  // without affecting the caller's `allValues` snapshot.
  const values: Record<string, number> = { ...allValues };

  let lambda = opts.dampingFactor;
  const errors: string[] = [];

  computeResidualVector(residuals, values, r);
  let rNormSq = normSq(r, m);

  // Already satisfied at the starting guess.
  if (rNormSq <= opts.tolerance * opts.tolerance) {
    return {
      success: true,
      iterations: 0,
      residualNorm: Math.sqrt(rNormSq),
      variables: values,
    };
  }

  let iteration = 0;
  let succeeded = false;

  for (iteration = 1; iteration <= opts.maxIterations; iteration++) {
    assembleNormalEquations(residuals, values, r, indexOf, lambda, n, H, g);

    // Solve H·dx = -g. First try Cholesky (H should be PD with λ>0);
    // fall back to Gauss if it reports singular (e.g., rank-deficient
    // despite damping, or numerical issues). Copy H because Cholesky
    // factors in place.
    HCopy.set(H);
    for (let i = 0; i < n; i++) dx[i] = -g[i]!;
    let ok = choleskySolve(HCopy, dx, n);
    if (!ok) {
      HCopy.set(H);
      for (let i = 0; i < n; i++) dx[i] = -g[i]!;
      ok = gaussianSolve(HCopy, dx, n);
    }
    if (!ok) {
      // Truly singular — bump damping and retry, up to maxDamping.
      lambda *= 10;
      if (lambda > opts.maxDamping) {
        errors.push("Singular system; damping exceeded maxDamping");
        break;
      }
      continue;
    }

    // Check step size — if too small, we're stuck.
    let dxNormSq = 0;
    for (let i = 0; i < n; i++) dxNormSq += dx[i]! * dx[i]!;
    if (dxNormSq < opts.minStep * opts.minStep) {
      // Converged as much as we can; report current state.
      if (rNormSq <= opts.tolerance * opts.tolerance) {
        succeeded = true;
      } else {
        errors.push(
          `Step below minStep (|dx|²=${dxNormSq.toExponential(3)}) but residual=${Math.sqrt(rNormSq).toExponential(3)}`,
        );
      }
      break;
    }

    // Trial step: save current values, overlay dx, evaluate residual.
    // If the trial residual is smaller we commit the new values;
    // otherwise we restore savedValues and increase damping.
    const savedValues: number[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const id = ids[i]!;
      savedValues[i] = values[id]!;
      values[id] = values[id]! + dx[i]!;
    }

    computeResidualVector(residuals, values, rTrial);
    const rTrialNormSq = normSq(rTrial, m);

    if (rTrialNormSq < rNormSq) {
      // Accept step.
      r.set(rTrial);
      rNormSq = rTrialNormSq;
      lambda = Math.max(lambda / 2, 1e-18);
      if (rNormSq <= opts.tolerance * opts.tolerance) {
        succeeded = true;
        break;
      }
    } else {
      // Reject — restore values, increase damping.
      for (let i = 0; i < n; i++) values[ids[i]!] = savedValues[i]!;
      lambda *= 10;
      if (lambda > opts.maxDamping) {
        errors.push(
          `Damping ${lambda.toExponential(3)} > maxDamping; constraints likely infeasible`,
        );
        break;
      }
    }
  }

  // If we exhausted iterations check final residual.
  if (!succeeded && rNormSq <= opts.tolerance * opts.tolerance) {
    succeeded = true;
  }
  if (!succeeded && iteration > opts.maxIterations) {
    errors.push(
      `Did not converge in ${opts.maxIterations} iterations (residual=${Math.sqrt(rNormSq).toExponential(3)})`,
    );
  }

  return {
    success: succeeded,
    iterations: Math.min(iteration, opts.maxIterations),
    residualNorm: Math.sqrt(rNormSq),
    variables: values,
    errors: errors.length > 0 ? errors : undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// Re-exports for convenience (so consumers import from solver entrypoint)
// ═══════════════════════════════════════════════════════════════════════

export type {
  ConstraintResidual,
  ConstraintVariable,
  SolveInput,
  SolveResult,
  SolverOptions,
  PointRef,
  LineRef,
  CircleRef,
  ArcRef,
} from "./constraintSolver.types";
